/**
 * MirrorListPage
 * 
 * Página de lista de requisições aguardando auditoria fiscal.
 * Exibe requisições que precisam de upload de espelho de nota ou que têm divergências.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, FileCheck,
  Calendar, Scale,
  AlertCircle, Clock, FileText
} from 'lucide-react';
import { mirrorService } from '../../../services/purchasesApi';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components';
import './MirrorListPage.css';

const MirrorListPage = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [mirrors, setMirrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending_upload, divergent, processing

  // Carregar lista de espelhos
  useEffect(() => {
    const loadMirrors = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await mirrorService.getList();
        if (response.success) {
          setMirrors(response.data || []);
        } else {
          setError(response.message || 'Erro ao carregar lista de espelhos');
          showToast(response.message || 'Erro ao carregar lista de espelhos', 'error');
        }
      } catch (err) {
        setError(err.message || 'Erro ao carregar lista de espelhos');
        showToast(err.message || 'Erro ao carregar lista de espelhos', 'error');
        setMirrors([]);
      } finally {
        setLoading(false);
      }
    };

    loadMirrors();
  }, [showToast]);

  // Filtrar espelhos
  const filteredMirrors = mirrors.filter(mirror => {
    const matchesSearch = !searchTerm || 
      mirror.requisition_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mirror.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mirror.title?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || mirror.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Obter badge de status
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_upload':
        return (
          <span className="mirror-status-badge mirror-status-pending">
            <Clock size={10} />
            Aguardando Doc
          </span>
        );
      case 'divergent':
        return (
          <span className="mirror-status-badge mirror-status-divergent">
            <AlertCircle size={10} />
            Match Divergente
          </span>
        );
      case 'processing':
        return (
          <span className="mirror-status-badge mirror-status-processing">
            <FileText size={10} />
            Em Auditoria
          </span>
        );
      default:
        return (
          <span className="mirror-status-badge mirror-status-pending">
            <Clock size={10} />
            {status || 'Aguardando'}
          </span>
        );
    }
  };

  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  // Formatar valor
  const formatValue = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="mirror-list-page">
      {/* Header */}
      <div className="finance-list-header">
        <div>
          <h1 className="finance-list-title">
            Auditoria <span className="finance-list-title-highlight">Fiscal</span>
          </h1>
          <p className="finance-list-subtitle">
            Validação de Espelhos de Nota via Gemini OCR
          </p>
        </div>
        <button 
          onClick={() => navigate('/erp/purchases')} 
          className="finance-list-back-button"
        >
          <ArrowLeft size={16} />
          Painel de Compras
        </button>
      </div>

      {/* Toolbar */}
      <div className="finance-list-toolbar">
        <div className="finance-list-search">
          <Search className="finance-list-search-icon" size={18} />
          <input
            type="text"
            placeholder="Buscar por fornecedor ou ID da requisição..."
            className="finance-list-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="finance-list-filters">
          <select
            className="finance-list-filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Todos os Status</option>
            <option value="pending_upload">Aguardando Doc</option>
            <option value="divergent">Match Divergente</option>
            <option value="processing">Em Auditoria</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="finance-list-loading">
          <div className="finance-list-loading-spinner"></div>
          <p>Carregando lista de espelhos...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="finance-list-error">
          <AlertCircle size={24} />
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="finance-list-retry-button"
          >
            Tentar Novamente
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredMirrors.length === 0 && (
        <div className="finance-list-empty">
          <FileCheck size={48} />
          <p>Nenhum espelho encontrado</p>
          <span>
            {searchTerm || filterStatus !== 'all' 
              ? 'Tente ajustar os filtros de busca'
              : 'Não há requisições aguardando auditoria fiscal'}
          </span>
        </div>
      )}

      {/* Grid de Cards */}
      {!loading && !error && filteredMirrors.length > 0 && (
        <div className="mirror-list-grid">
          {filteredMirrors.map((mirror) => {
            const statusClass = mirror.status || 'pending';
            return (
              <button
                key={mirror.id || mirror.requisition_id}
                onClick={() => navigate(`/erp/purchases/mirrors/verify/${mirror.requisition_id || mirror.id}`)}
                className="mirror-list-card"
              >
                {/* Background Glow based on status */}
                <div className={`mirror-list-card-glow status-${statusClass}`} />

                <div className="mirror-list-card-header">
                  <div className="mirror-list-card-badges">
                    {getStatusBadge(mirror.status)}
                  </div>
                  <span className="mirror-list-card-id">
                    {mirror.requisition_id || `RQ-${mirror.id}`}
                  </span>
                </div>
                
                <h3 className="mirror-list-card-title">
                  {mirror.supplier_name || mirror.vendor || 'Fornecedor não informado'}
                </h3>
                
                <div className="mirror-list-card-meta">
                  <div className="mirror-list-card-meta-item">
                    <Calendar size={12} />
                    <span>{formatDate(mirror.date || mirror.created_at)}</span>
                  </div>
                  {mirror.title && (
                    <div className="mirror-list-card-meta-item">
                      <span>{mirror.title}</span>
                    </div>
                  )}
                </div>

                <div className="mirror-list-card-footer">
                  <div className="mirror-list-card-value">
                    <p className="mirror-list-card-label">Valor Acordado</p>
                    <p className="mirror-list-card-value-amount">
                      {formatValue(mirror.total_value || mirror.totalValue || 0)}
                    </p>
                  </div>
                  <div className="mirror-list-card-icon">
                    <Scale size={18} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Toast */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  );
};

export default MirrorListPage;
